/**
 * DriverLayoutTemplate — шаблон для PWA-режиму водія (ROLE_DRIVER)
 *
 * Автоматично реєструється системою LayoutTemplateLoader як шаблон 'driver'
 * (через import.meta.glob: /src/Ship/Templates/Layouts/*LayoutTemplate.{tsx,jsx}).
 *
 * Для підключення шаблону до маршруту — вкажіть `template: 'driver'` в pageConfig.
 *
 * Доступ обмежено роллю ROLE_DRIVER. Всі інші авторизовані користувачі отримають LoginForm.
 */

import React, { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'

import { userManager } from '@/Core/Managers'
import type { UIManager } from '@/Core/Managers/UI'
import { restClient } from '@/Core'

import { CentrifugoConnector } from './Components/CentrifugoConnector'
import { DefaultLoadTemplate } from './Components/DefaultLoadTemplate'
import { PwaHeader } from './Components/PwaHeader/PwaHeader'
import { DriverBottomNavigation } from './Components/DriverBottomNavigation/DriverBottomNavigation'
import { useDriverAssignmentsRealtime } from '@/Containers/DriverSection/DriverDashboardContainer/Hooks/useDriverAssignmentsRealtime'
import { driverDashboardManager } from '@/Containers/DriverSection/DriverDashboardContainer/Stores/DriverDashboardManager'
import { LoadUnreadCountTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/LoadUnreadCountTask'
import { LoadTabUnreadCountsTask } from '@/Containers/CommunicationSection/NotificationContainer/Tasks/LoadTabUnreadCountsTask'
import { ServiceWorkerRegistrar } from '@/Ship/Pwa/ServiceWorkerRegistrar'
import { OfflineBanner } from '@/Ship/Pwa/Components/OfflineBanner'
import { UpdatePrompt } from '@/Ship/Pwa/Components/UpdatePrompt'
import { SyncErrorAlert } from '@/Ship/Pwa/Components/SyncErrorAlert'
import { pwaManager } from '@/Ship/Pwa/PwaManager'
import { NOTIFICATION_CLICK_MESSAGE } from '@/Ship/Pwa/notificationClick'
import { useSyncOnReconnect } from '@/Ship/Pwa/OfflineSync'
import { AppConfig } from '@/Ship/Configs/AppConfig'
import { isAuthFailure } from '@/Ship/Helpers/isAuthFailure'
import { useRouteDocumentTitle } from '@/Ship/Hooks/useRouteDocumentTitle'
import { themeStore } from '@/Ship/Stores/ThemeStore'
import { LoginForm } from '@/Containers/PublicSection/AuthContainer/Components/LoginForm'

import styles from './Components/PwaLayout/PwaLayout.module.css'
import '@/Ship/Pwa/Styles/pwa.css'
import { driverThemeStore } from '@/Ship/Pwa/DriverThemeStore'

interface DriverLayoutTemplateProps {
    layoutContext?: UIManager
    children: React.ReactNode
}

// ── Auth guard — ROLE_DRIVER тільки ────────────────────────────────────────
export const accessFn = () => userManager.isAuthorized && userManager.hasRole('ROLE_DRIVER')

// ── 403 component ──────────────────────────────────────────────────────────
// Авторизований, але не водій → редирект на головну
// Не авторизований → форма входу
export const templateError403 = () => {
    if (userManager.isAuthorized) {
        return <Navigate to="/" replace />
    }
    return <LoginForm />
}

// ── Data loading — same as PwaLayoutTemplate ───────────────────────────────
export const loadData = async () => {
    const startTime = Date.now()
    const minDuration = 1000

    // Чекаємо гідратацію стану з localStorage перед перевіркою isAuthorized.
    // Без цього при холодному старті (особливо offline, коли loadData
    // не робить запит /me і завершується миттєво) isAuthorized ще має
    // дефолтне значення false — і водія помилково викидає на форму входу.
    await userManager.waitForHydration()

    if (userManager.isAuthorized) {
        // При відсутності інтернету — пропускаємо запит /me,
        // довіряємо збереженому стану isAuthorized з localStorage.
        if (!navigator.onLine) {
            const elapsed = Date.now() - startTime
            const remaining = minDuration - elapsed
            if (remaining > 0) {
                await new Promise((resolve) => setTimeout(resolve, remaining))
            }
        } else {
            try {
                const user = await restClient.get(AppConfig.restUserMe)

                if (!(user as any)?.id) {
                    userManager.logout(true)
                }

                const elapsed = Date.now() - startTime
                const remaining = minDuration - elapsed
                if (remaining > 0) {
                    await new Promise((resolve) => setTimeout(resolve, remaining))
                }
            } catch (error: any) {
                console.error('[DriverLayoutTemplate loadData] Error:', { error })
                // Розлогінюємо ЛИШЕ при явній відмові автентифікації.
                //
                // Раніше тут був зворотний список "транзиентних" помилок, і будь-яка
                // інша — зокрема 500/502/504 — означала logout. Через це один деплой
                // бекенду чи перезапуск Traefik викидав усіх водіїв одночасно.
                // Серверна помилка не означає, що сесія водія недійсна.
                //
                // 401 сюди доходить лише якщо не спрацював retry з оновленням токена
                // (див. BaseHttpClient) — тобто сесія справді мертва.
                if (isAuthFailure(error)) {
                    userManager.logout(true)
                }
            }
        }
    }

    const elapsed = Date.now() - startTime
    const remaining = minDuration - elapsed
    if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining))
    }

    return null
}

// ── Loading skeleton ────────────────────────────────────────────────────────
export const loadTemplate = DefaultLoadTemplate

// ── Main template component ─────────────────────────────────────────────────
export const DriverLayoutTemplate: React.FC<DriverLayoutTemplateProps> = observer(({ children }) => {
    // Realtime: на нове сповіщення (напр. "Транспорт розподілено") оновлюємо
    // і список завдань на Головній, щоб він не розходився зі сповіщеннями.
    const { onUserNotificationCreated } = useDriverAssignmentsRealtime()
    const navigate = useNavigate()
    // «Переглянути завдання» у тості — картка водія (шторка на Головній), як зі сторінки
    // сповіщень, а не CRM-картка AssignmentResource, яку тост відкриває в адмінці
    const openAssignment = (uuid: string) => {
        driverDashboardManager.requestOpenByUuid(uuid)
        navigate('/driver')
    }
    // Назва вкладки = назва розділу (pageConfig.title поточної сторінки)
    useRouteDocumentTitle()

    // Тема кабінету водія — його власний вибір (DriverThemeStore), незалежний від
    // теми адмінки в тому ж браузері; при виході з кабінету повертаємо адмінську.
    // data-pwa на <html>: antd рендерить модалки й дровери в body, поза нашим контейнером,
    // а стилі PWA для них (pwa.css) шукають саме цей атрибут
    useEffect(() => {
        driverThemeStore.apply()
        document.documentElement.setAttribute('data-pwa', 'driver')
        return () => {
            document.documentElement.removeAttribute('data-pwa')
            themeStore.restoreMode()
        }
    }, [])

    // Тап по push-сповіщенню, коли кабінет уже відкритий: service worker просить перейти за
    // адресою сповіщення (картку завдання за ?assignment= відкриває Головна)
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return undefined
        const onMessage = (event: MessageEvent) => {
            if (event.data?.type === NOTIFICATION_CLICK_MESSAGE && typeof event.data.url === 'string') {
                navigate(event.data.url)
            }
        }
        navigator.serviceWorker.addEventListener('message', onMessage)
        return () => navigator.serviceWorker.removeEventListener('message', onMessage)
    }, [navigate])

    // Автоматична синхронізація при відновленні з'єднання
    useSyncOnReconnect()

    // Первинне завантаження лічильників непрочитаних при старті застосунку.
    // Без цього після холодного завантаження/перезапуску бейдж на кнопці
    // "Сповіщення" і лічильники вкладок порожні, доки водій не відкриє
    // сторінку сповіщень (де вони й рахувались раніше). Тепер вони
    // підтягуються одразу при монтуванні layout.
    useEffect(() => {
        if (!navigator.onLine) return
        LoadUnreadCountTask()
        LoadTabUnreadCountsTask()
    }, [])

    // Примусової зміни пароля в кабінеті водія немає (рішення замовника 2026-10-28): пароль
    // водію задає адмін у картці співробітника, прапорець mustChangePassword не враховується

    // Сповіщення для водія відкриваються з нижнього меню (DriverBottomNavigation
    // → сторінка Driver.Notifications). Іконку-дублікат у шапці прибрано, щоб не
    // було двох різних точок входу з відмінною поведінкою (drawer vs сторінка).

    return (
        <>
            <CentrifugoConnector
                onUserNotificationCreated={onUserNotificationCreated}
                onOpenAssignment={openAssignment}
            />
            <ServiceWorkerRegistrar />

            <div className={styles['layout']}>
                <PwaHeader />
                <OfflineBanner />
                <SyncErrorAlert />
                <div
                    className={`${styles['content']} ${styles['contentDriverNav']}${pwaManager.isOffline ? ` ${styles['contentOffline']}` : ''}`}
                >
                    <main className={styles['main']}>{children}</main>
                </div>
                <UpdatePrompt />
                <DriverBottomNavigation />
            </div>
        </>
    )
})

DriverLayoutTemplate.displayName = 'DriverLayoutTemplate'
